/**
 * Cache Cleanup Service
 *
 * Handles clearing local caches on logout while preserving essential user preferences
 * that should persist across sessions (language, theme, remember me).
 */

import { userPreferencesService } from './userPreferencesService';
import { clearSecureStorage } from './secureTokenStorage';
import { SessionStorage } from './storage';
import { queryClient } from '../config/queryClient';

// Custom event dispatched after cache cleanup
export const CACHE_CLEARED_EVENT = 'cache-cleared-on-logout';

// Keys to preserve on logout (not user-specific data)
const PRESERVED_KEYS = [
  // Language preferences
  'language',
  'languageUserChosen',
  // Theme preference
  'darkMode',
  // Remember Me (email only, not auth tokens)
  'remember-me-email',
  'remember-me-enabled',
  // PWA install state
  'pwa-install-dismissed',
  // App version
  'app_version',
  // Cookie consent
  'cookie-consent',
];

// User-specific keys that are explicitly cleared (for documentation)
// These are all cleared by localStorage.clear(), listed here for reference
const USER_SPECIFIC_KEYS = [
  // Monitor wallets
  'fluxnode_wallets',
  'fluxnode_purchase_tracking',
  'fluxnode_apps_owner',
  'cronos_wallets',
  'cronos_early_bird_supporters',
  'hedera_accounts',
  // Portfolio data
  'portfolios',
  'active_portfolio',
  'edouard_portfolio_snapshots',
  'portfolio_dashboard_layout',
  // User preferences
  'user_mode',
  'show_btc_comparison',
  'user_preferences',
  // Alerts
  'price_alerts',
  // iPad watchlist
  'dashboard_ipad_watchlist',
  // Advisor conversations
  'advisor_conversations_local',
  // Academy progress
  'wealthhub_academy_progress',
  'wealthhub_academy_my_list',
  'wealthhub_academy_path_progress',
  'edouard_ai_academy_progress',
  'edouard_ai_academy_my_list',
  'edouard_ai_academy_achievements',
  // Quiz progress
  'wealthhub_quiz_progress',
  // Feature discovery
  'discovered_features',
  // Page visibility
  'page_visibility_prefs',
  // Votes
  'edouard_project_votes',
  'edouard_project_votes_user',
  // Auth storage (handled by Zustand persist)
  'auth-storage',
];

/**
 * Clear all local caches while preserving essential preferences
 * Call this on logout to ensure user data is properly cleaned up
 */
export function clearAllCachesOnLogout(): void {
  console.log('[CacheCleanup] Starting cache cleanup on logout...');

  // 1. Save preserved values
  const preservedValues: Record<string, string | null> = {};
  PRESERVED_KEYS.forEach((key) => {
    preservedValues[key] = localStorage.getItem(key);
  });

  // 2. Clear secure token storage (in-memory tokens and encrypted storage)
  try {
    clearSecureStorage();
    console.log('[CacheCleanup] Cleared secure token storage');
  } catch (err) {
    console.error('[CacheCleanup] Error clearing secure storage:', err);
  }

  // 3. Clear user preferences service cache
  try {
    userPreferencesService.clearCache();
    console.log('[CacheCleanup] Cleared user preferences cache');
  } catch (err) {
    console.error('[CacheCleanup] Error clearing preferences cache:', err);
  }

  // 4. Clear React Query cache (all in-memory query data)
  try {
    queryClient.clear();
    console.log('[CacheCleanup] Cleared React Query cache');
  } catch (err) {
    console.error('[CacheCleanup] Error clearing React Query cache:', err);
  }

  // 5. Clear all localStorage
  try {
    localStorage.clear();
    console.log('[CacheCleanup] Cleared localStorage (including monitor wallets, portfolios, alerts, etc.)');
  } catch (err) {
    console.error('[CacheCleanup] Error clearing localStorage:', err);
  }

  // 6. Restore preserved values
  Object.entries(preservedValues).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
  console.log('[CacheCleanup] Restored preserved preferences:', Object.keys(preservedValues).filter((k) => preservedValues[k] !== null));

  // 7. Clear sessionStorage
  try {
    SessionStorage.clear();
    console.log('[CacheCleanup] Cleared sessionStorage');
  } catch (err) {
    console.error('[CacheCleanup] Error clearing sessionStorage:', err);
  }

  // 8. Dispatch event for components to reset their in-memory state
  try {
    window.dispatchEvent(new CustomEvent(CACHE_CLEARED_EVENT));
    console.log('[CacheCleanup] Dispatched cache-cleared event');
  } catch (err) {
    console.error('[CacheCleanup] Error dispatching event:', err);
  }

  console.log('[CacheCleanup] Cache cleanup complete');
}

/**
 * Get list of preserved keys (for reference/debugging)
 */
export function getPreservedKeys(): string[] {
  return [...PRESERVED_KEYS];
}

/**
 * Get list of user-specific keys that are cleared (for reference/debugging)
 */
export function getUserSpecificKeys(): string[] {
  return [...USER_SPECIFIC_KEYS];
}
